import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { EntityTypeEnum, OrganisationType, TradingEntity, TradingEntityMeta, TradingRelationshipDetail, WorkflowActions } from 'api'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { createWorkflowTradingEntityMetasSelector, createWorkflowTradingRelationshipDetailsSelector } from 'selectors/workflowSelectors'
import { AgGridWrapper } from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { TradingEntityRelationshipsDialog } from './TradingEntityRelationshipsDialog'
import {
  tradingEntityRelationshipsGridOptions,
  tradingEntityRelationshipsPanelColumnDefs
} from './TradingEntityRelationshipsPanelGridModel'
import { GridHeader, Wrapper } from './TradingEntityRelationshipPanelStyled'

type TradingRelationshipsPanelProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  tradingEntity: TradingEntity
  workflowId: string
  organisationType: OrganisationType
  organisationId: string
  readOnly: boolean
}

interface StateProps {
  tradingRelationshipDetails: TradingRelationshipDetail[]
  tradingEntityMetas: TradingEntityMeta[]
}

interface DispatchProps {
  addNewTradingEntityRelationship: (
    workflowId: string,
    entityTypeEnum: EntityTypeEnum,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: TradingRelationshipsPanelProps) => ownProps.workflowId
const organisationTypeSelector = (_state: {}, ownProps: TradingRelationshipsPanelProps) => ownProps.organisationType
const tradingEntityIdSelector = (_state: {}, ownProps: TradingRelationshipsPanelProps) => ownProps.tradingEntity.id

const mapStateToProps = createStructuredSelector({
  tradingRelationshipDetails: createWorkflowTradingRelationshipDetailsSelector(
    workflowIdSelector,
    organisationTypeSelector,
    tradingEntityIdSelector
  ),
  tradingEntityMetas: createWorkflowTradingEntityMetasSelector(workflowIdSelector)
})

const mapDispatchToProps = {
  addNewTradingEntityRelationship: WorkflowActions.createAddToExistingWorkflow
}

const TradingEntityRelationshipsPanelComponent: React.FunctionComponent<TradingRelationshipsPanelProps> = props => {
  const {
    tradingEntity,
    workflowId,
    organisationType,
    readOnly,
    tradingRelationshipDetails,
    tradingEntityMetas,
    showModal,
    organisationId
  } = props

  const handleNewTradingEntityRelationship = () => {
    const { addNewTradingEntityRelationship } = props

    const existingRelationshipsTradingEntityIds = tradingRelationshipDetails.map(({ tradingRelationship }) =>
      organisationType === OrganisationType.BUY_SIDE ? tradingRelationship.sellTradingEntity : tradingRelationship.buyTradingEntity
    )

    const correlatedResponseCallback = (_workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      return showModal(
        <TradingEntityRelationshipsDialog
          organisationType={organisationType}
          organisationId={organisationId}
          tradingEntityId={tradingEntity.id}
          workflowId={_workflowId}
          entityId={newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_RELATIONSHIP]}
          existingRelationshipsTradingEntityIds={existingRelationshipsTradingEntityIds}
        />
      )
    }

    addNewTradingEntityRelationship(workflowId, EntityTypeEnum.TRADING_RELATIONSHIP, correlatedResponseCallback)
  }

  const noTradingEntityRelationshipsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_TRADING_ENTITY_RELATIONSHIPS)}</div>

  return (
    <Wrapper>
      <GridHeader>
        <label>{Locale.translate(Translation.TC_TRADING_ENTITY_RELATIONSHIPS)}</label>
        <TextButton
          dataTestId={'TradingEntityRelationshipsPanel-TextButton-AddNewRelationship'}
          type={TextButtonType.Primary}
          onClick={handleNewTradingEntityRelationship}
          disabled={readOnly}>
          {Locale.translate(Translation.TC_NEW_TRADING_ENTITY_RELATIONSHIP)}
        </TextButton>
      </GridHeader>
      <AgGridWrapper
        className="datagrid-borderless"
        gridOptions={tradingEntityRelationshipsGridOptions}
        columnDefs={tradingEntityRelationshipsPanelColumnDefs}
        rowData={tradingRelationshipDetails}
        placeholder={noTradingEntityRelationshipsPlaceholder}
        context={{
          workflowId,
          tradingEntityId: tradingEntity.id,
          organisationId,
          tradingEntityMetas
        }}
      />
    </Wrapper>
  )
}

export const TradingEntityRelationshipsPanel = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradingEntityRelationshipsPanelComponent)
)
